import { jsx as _jsx } from "react/jsx-runtime";
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { createPluginFactory } from '#pplx/frontend/composer-sdk/plugins/createPluginFactory.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { useBooleanFlagValue } from '#pplx/frontend/experimentation/hooks.js';
import React from 'react';
const RestrictedSlot = Dynamic(() => import('#pplx/frontend/apps/ask/restricted-feature-remotebox-execution-target/index.js'), { restricted: true });
const createComputerRemoteBoxExecutionTargetPlugin = createPluginFactory({
    name: 'remotebox-execution-target',
    capabilities: [
        {
            kind: 'slot',
            components: [{ slot: 'chin', component: RestrictedSlot }],
        },
    ],
});
const createFollowUpMenuPlugin = createPluginFactory({
    name: 'remotebox-execution-target',
    capabilities: [
        {
            kind: 'slot',
            components: [
                {
                    slot: 'leftAttribution',
                    component: (props) => props.readOnly ? null : _jsx(RestrictedSlot, { ...props, presentation: "pending-chip" }),
                },
            ],
        },
        {
            kind: 'plusMenu',
            items: async () => [
                {
                    id: 'environment',
                    // The persistent chip slot owns configuration; closing this menu must not clear it.
                    render: ({ props }) => (_jsx(RestrictedSlot, { ...props, onWorkflowConfigChange: undefined, presentation: "menu" })),
                },
            ],
        },
    ],
});
/** Reports whether this authenticated user can choose an environment on an eligible surface. */
export function useComputerRemoteBoxExecutionTargetEnabled(eligible) {
    const isUserLoggedIn = useIsUserLoggedIn();
    return useBooleanFlagValue({
        // The desktop app rolls out separately from web, like the native Mac picker.
        flag: import.meta.env.VITE_IS_PERPLEXITY_DESKTOP_BUILD
            ? 'xplat-computer-remotebox-picker'
            : 'computer-remotebox-picker',
        defaultValue: false,
        skip: !isUserLoggedIn || !eligible,
    }).variation;
}
/**
 * Provides an optional environment control and initial task configuration.
 * Pass a null mode while the surface's task identity is unresolved.
 */
export function useComputerRemoteBoxExecutionTargetAddon({ eligible, mode, workflowConfig, }) {
    const enabled = useComputerRemoteBoxExecutionTargetEnabled(eligible && mode !== null);
    // The restricted slot clears its contribution when it unmounts or its task identity changes.
    // Until that cleanup runs, only an enabled surface may use a stored contribution.
    const { session } = useSession();
    const projectUuid = mode?.mode === 'new-task' ? mode.projectUuid : undefined;
    const scope = `${session?.user?.id}:${session?.user?.org_uuid}:${mode?.mode === 'follow-up' ? mode.sessionId : `new-task:${projectUuid ?? ''}`}`;
    const [contribution, setContribution] = React.useState();
    const onWorkflowConfigChange = React.useCallback((config) => setContribution({ scope, config }), [scope]);
    // The menu unmounts when closed; its editable choice belongs to the composer lifetime.
    const [choice, setChoice] = React.useState();
    const selection = React.useMemo(() => ({ choice, onChange: setChoice }), [choice]);
    const sessionId = mode?.mode === 'follow-up' ? mode.sessionId : null;
    const isTurnActive = mode?.mode === 'follow-up' && mode.isTurnActive === true;
    const switchCount = mode?.mode === 'follow-up' ? (mode.switchCount ?? 0) : 0;
    const readOnly = mode?.mode === 'follow-up' && mode.readOnly === true;
    const activeContribution = enabled && mode !== null && !readOnly && contribution?.scope === scope
        ? contribution.config
        : undefined;
    React.useEffect(() => {
        setChoice(undefined);
    }, [sessionId, enabled, readOnly]);
    const plugin = React.useMemo(() => {
        if (!enabled)
            return null;
        if (sessionId === null) {
            return createComputerRemoteBoxExecutionTargetPlugin({
                mode: 'new-task',
                projectUuid,
                onWorkflowConfigChange,
            });
        }
        if (readOnly) {
            return createFollowUpMenuPlugin({
                mode: 'follow-up',
                sessionId,
                isTurnActive,
                readOnly: true,
            });
        }
        return createFollowUpMenuPlugin({
            mode: 'follow-up',
            sessionId,
            isTurnActive,
            switchCount,
            selection,
            onWorkflowConfigChange,
        });
    }, [
        enabled,
        sessionId,
        projectUuid,
        isTurnActive,
        switchCount,
        readOnly,
        selection,
        onWorkflowConfigChange,
    ]);
    const props = plugin?.props;
    return {
        plugin,
        addon: props?.mode === 'new-task' ? _jsx(RestrictedSlot, { ...props }) : null,
        followUpProps: props?.mode === 'follow-up' ? props : undefined,
        workflowConfig: activeContribution
            ? { ...workflowConfig, ...activeContribution }
            : workflowConfig,
    };
}
//# sourceMappingURL=useComputerRemoteBoxExecutionTargetAddon.js.map